export const CLIENT_CACHE_SEQUENCE_FEATURE = 'client-cache-sequence'
export const CLIENT_CACHE_SEQUENCE_STORAGE_KEY = 'ae-client-cache-sequence'

type CacheSequenceEnvironment = {
  localStorage: Storage
  sessionStorage: Storage
  caches?: CacheStorage
  indexedDB?: IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>
  }
  navigator?: Navigator & {
    storage?: StorageManager & {
      getDirectory?: () => Promise<FileSystemDirectoryHandle>
    }
    serviceWorker?: ServiceWorkerContainer
  }
  location: Location
}

/**
 * Reset origin-scoped browser caches after an administrator changes the
 * server's client-cache-sequence feature option. Cookies are intentionally
 * left alone, so the user remains authenticated after the reload.
 */
export async function applyClientCacheSequence(
  sequence: unknown,
  environment: CacheSequenceEnvironment = globalThis as unknown as CacheSequenceEnvironment,
  beforeReset?: () => Promise<void>|void,
  coordinatedReset?: () => Promise<unknown>,
): Promise<boolean> {
  if (sequence == null || sequence === '') {
    return false
  }

  const sequenceValue = String(sequence)
  const previousSequence = environment.localStorage.getItem(CLIENT_CACHE_SEQUENCE_STORAGE_KEY)
  if (previousSequence === sequenceValue) {
    return false
  }

  if (coordinatedReset) {
    environment.localStorage.setItem(CLIENT_CACHE_SEQUENCE_STORAGE_KEY, sequenceValue)
    try {
      await coordinatedReset()
    } catch (error) {
      if (previousSequence == null) {
        environment.localStorage.removeItem(CLIENT_CACHE_SEQUENCE_STORAGE_KEY)
      } else {
        environment.localStorage.setItem(CLIENT_CACHE_SEQUENCE_STORAGE_KEY, previousSequence)
      }
      throw error
    }
    return true
  }

  await beforeReset?.()

  const serviceWorkerStillControlsPage = await unregisterServiceWorkers(environment.navigator?.serviceWorker)
  if (serviceWorkerStillControlsPage) {
    // unregister() does not detach the current document from its controller.
    // Navigation does; after this reload the sequence still differs, so boot
    // returns here and clears storage once the old worker no longer controls it.
    environment.location.reload()
    return true
  }

  await Promise.all([
    clearCacheStorage(environment.caches),
    clearIndexedDatabases(environment.indexedDB),
    clearOriginPrivateFileSystem(environment.navigator?.storage),
  ])

  environment.localStorage.clear()
  environment.sessionStorage.clear()
  environment.localStorage.setItem(CLIENT_CACHE_SEQUENCE_STORAGE_KEY, sequenceValue)
  environment.location.reload()

  return true
}

async function clearCacheStorage(cacheStorage?: CacheStorage): Promise<void> {
  if (!cacheStorage) {
    return
  }

  const cacheNames = await cacheStorage.keys()
  await Promise.all(cacheNames.map(cacheName => cacheStorage.delete(cacheName)))
}

async function unregisterServiceWorkers(serviceWorker?: ServiceWorkerContainer): Promise<boolean> {
  if (!serviceWorker) {
    return false
  }

  const registrations = await serviceWorker.getRegistrations()
  await Promise.all(registrations.map(registration => registration.unregister()))
  return serviceWorker.controller != null
}

async function clearIndexedDatabases(indexedDB?: CacheSequenceEnvironment['indexedDB']): Promise<void> {
  if (!indexedDB?.databases) {
    return
  }

  const databases = await indexedDB.databases()
  await Promise.all(databases
    .map(database => database.name)
    .filter((name): name is string => Boolean(name))
    .map(name => deleteIndexedDatabase(indexedDB, name)))
}

function deleteIndexedDatabase(indexedDB: IDBFactory, name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error || new Error(`Could not delete IndexedDB database ${name}`))
    request.onblocked = () => reject(new Error(`Deleting IndexedDB database ${name} is blocked by another tab`))
  })
}

async function clearOriginPrivateFileSystem(storage?: CacheSequenceEnvironment['navigator']['storage']): Promise<void> {
  if (!storage?.getDirectory) {
    return
  }

  const root = await storage.getDirectory()
  for await (const entry of root.values()) {
    await root.removeEntry(entry.name, { recursive: true })
  }
}

/** Clear only Aeppic caches; preserve credentials, offline handles and support records. */
export async function clearAeppicClientCaches(): Promise<void> {
  if (globalThis.caches) {
    for (const name of await caches.keys()) {
      if (/^(aeppic|ae-)/i.test(name)) await caches.delete(name)
    }
  }
  if (navigator.storage?.getDirectory) {
    const root = await navigator.storage.getDirectory()
    for await (const entry of root.values()) {
      if (/^aeppic-(read-model|asset|model-journal)/.test(entry.name)) {
        try {
          await root.removeEntry(entry.name, { recursive: true })
        } catch (error) {
          // Another prepared tab may already have removed this shared cache.
          if ((error as DOMException).name !== 'NotFoundError') throw error
        }
      }
    }
  }
  for (const storage of [localStorage, sessionStorage]) {
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
    for (const key of keys) {
      // This records the completed reset, not cached data. Keep it so boot
      // does not request another reset for the same server sequence.
      if (key === CLIENT_CACHE_SEQUENCE_STORAGE_KEY) {
        continue
      }
      if (key && /(?:cache|lookup|read-model|local-model)/i.test(key) && /^(aeppic|ae-)/i.test(key)) {
        storage.removeItem(key)
      }
    }
  }
}
